import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Leaf, Search, Filter, Sun, Droplets, Clock, Calendar, Users, XCircle,
  ChevronRight, Loader2, Sprout, TreeDeciduous, Flower2, MapPin, Printer,
  BookOpen, Info, X, ChevronDown, Thermometer, Apple
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tropical Calypso Colors
const tropical = {
  green: { primary: "#00A86B", vibrant: "#32DE84", dark: "#006B3C", light: "#D0F0C0" },
  mango: { primary: "#FF9F1C", vibrant: "#FFD93D", light: "#FFBF69" },
  plum: { primary: "#9B59B6", light: "#D7BDE2" },
  guava: { primary: "#FF6B6B" },
  coconut: { cream: "#FFFBEB" }
};

const USDA_ZONES = ["3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const PlantDatabasePage = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    sun: "",
    water: "",
    plant_type: "",
    zone: ""
  });
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [spacingCalc, setSpacingCalc] = useState({ area: 100, result: null });
  const [viewMode, setViewMode] = useState("grid"); // grid, calendar
  const [calendarData, setCalendarData] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  useEffect(() => {
    fetchPlants();
  }, [filters]);

  const fetchPlants = async () => {
    setLoading(true);
    try {
      let url = `${API}/plants/database?`;
      if (filters.category) url += `category=${filters.category}&`;
      if (filters.sun) url += `sun=${filters.sun}&`;
      if (filters.water) url += `water=${filters.water}&`;
      if (filters.plant_type) url += `plant_type=${filters.plant_type}&`;
      if (filters.zone) url += `zone=${filters.zone}&`;
      
      const response = await axios.get(url);
      setPlants(response.data);
    } catch (error) {
      toast.error("Failed to load plant database");
    } finally {
      setLoading(false);
    }
  };

  const fetchHarvestCalendar = async () => {
    setLoadingCalendar(true);
    try {
      let url = `${API}/harvest-calendar?`;
      if (filters.zone) url += `zone=${filters.zone}&`;
      if (filters.category) url += `category=${filters.category}&`;
      
      const response = await axios.get(url);
      setCalendarData(response.data.calendar || []);
    } catch (error) {
      toast.error("Failed to load harvest calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    if (viewMode === "calendar") {
      fetchHarvestCalendar();
    }
  }, [viewMode, filters.zone, filters.category]);

  const filteredPlants = plants.filter(plant =>
    plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    plant.scientific_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const calculateSpacing = async (plantName) => {
    try {
      const response = await axios.get(`${API}/plants/spacing-calculator?plant_name=${encodeURIComponent(plantName)}&area_sqft=${spacingCalc.area}`);
      setSpacingCalc(prev => ({ ...prev, result: response.data }));
    } catch (error) {
      toast.error("Failed to calculate spacing");
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "vegetable": return <Sprout className="w-5 h-5" style={{ color: tropical.green.primary }} />;
      case "herb": return <Leaf className="w-5 h-5" style={{ color: tropical.green.vibrant }} />;
      case "fruit": return <Apple className="w-5 h-5" style={{ color: tropical.guava.primary }} />;
      case "flower": return <Flower2 className="w-5 h-5" style={{ color: tropical.plum.primary }} />;
      case "tree": return <TreeDeciduous className="w-5 h-5" style={{ color: tropical.green.dark }} />;
      default: return <Leaf className="w-5 h-5" style={{ color: tropical.green.primary }} />;
    }
  };

  const getSunIcon = (requirement) => {
    switch (requirement) {
      case "full_sun": return "☀️";
      case "partial_shade": return "⛅";
      case "full_shade": return "🌥️";
      default: return "☀️";
    }
  };

  const getWaterIcon = (needs) => {
    switch (needs) {
      case "high": return "💧💧💧";
      case "moderate": return "💧💧";
      case "low": return "💧";
      default: return "💧💧";
    }
  };

  const handlePrintCalendar = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>ROOTSPACE Harvest Calendar${filters.zone ? ` - Zone ${filters.zone}` : ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #00A86B; margin-bottom: 5px; }
            h2 { color: #666; font-size: 14px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: linear-gradient(135deg, #00A86B, #006B3C); color: white; }
            .plant-month { background: #D0F0C0; text-align: center; }
            .harvest-month { background: #FFBF69; text-align: center; }
            .category-row { background: #f5f5f5; font-weight: bold; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <h1>🌱 ROOTSPACE Harvest Calendar</h1>
          <h2>${filters.zone ? `USDA Zone ${filters.zone}` : 'All Zones'} ${filters.category ? `• ${filters.category.charAt(0).toUpperCase() + filters.category.slice(1)}s` : ''}</h2>
          <table>
            <thead>
              <tr>
                <th>Plant</th>
                <th>Type</th>
                ${MONTHS.map(m => `<th style="text-align:center">${m}</th>`).join('')}
                <th>Days</th>
              </tr>
            </thead>
            <tbody>
              ${calendarData.map(plant => `
                <tr>
                  <td><strong>${plant.name}</strong></td>
                  <td>${plant.plant_type || 'annual'}</td>
                  ${[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                    const isPlanting = plant.planting_months?.includes(m);
                    const isHarvest = plant.harvest_months?.includes(m);
                    return `<td class="${isPlanting ? 'plant-month' : isHarvest ? 'harvest-month' : ''}" style="text-align:center">${isPlanting ? '🌱' : isHarvest ? '🥬' : ''}</td>`;
                  }).join('')}
                  <td>${plant.days_to_harvest || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 12px; color: #666;">
            🌱 = Planting Time &nbsp;&nbsp; 🥬 = Harvest Time
          </p>
          <p style="font-size: 10px; color: #999; margin-top: 10px;">
            Generated by ROOTSPACE • rootspace.pro
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const openPlantDetails = (plant) => {
    setSelectedPlant(plant);
    setShowPlantModal(true);
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        background: `linear-gradient(160deg, ${tropical.coconut.cream} 0%, ${tropical.green.light}50 50%, ${tropical.mango.light}30 100%)`
      }}
    >
      {/* Header */}
      <header 
        className="border-b sticky top-0 z-40"
        style={{
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(12px)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>
              ← Back
            </Button>
            <div>
              <h1 
                className="text-xl font-bold flex items-center gap-2"
                style={{ color: tropical.green.dark }}
              >
                <Leaf className="w-6 h-6" style={{ color: tropical.green.primary }} />
                Plant Database
              </h1>
              <p className="text-sm text-gray-500">
                300+ vegetables, herbs, fruits, tropical plants & trees
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
              style={viewMode === "grid" ? { background: tropical.green.primary } : {}}
            >
              <Sprout className="w-4 h-4 mr-1" /> Plants
            </Button>
            <Button
              variant={viewMode === "calendar" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("calendar")}
              style={viewMode === "calendar" ? { background: tropical.mango.primary } : {}}
            >
              <Calendar className="w-4 h-4 mr-1" /> Harvest Calendar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="border-0 shadow-lg" style={{ background: 'rgba(255,255,255,0.9)' }}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: tropical.green.dark }}>
                  <Filter className="w-5 h-5" style={{ color: tropical.green.primary }} />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search plants..."
                    className="pl-9 rounded-xl"
                  />
                </div>

                {/* USDA Zone Filter - NEW */}
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <MapPin className="w-4 h-4" style={{ color: tropical.guava.primary }} />
                    USDA Zone
                  </label>
                  <select
                    value={filters.zone}
                    onChange={(e) => setFilters(f => ({ ...f, zone: e.target.value }))}
                    className="w-full rounded-xl border p-2.5"
                  >
                    <option value="">All Zones</option>
                    {USDA_ZONES.map(z => (
                      <option key={z} value={z}>Zone {z}</option>
                    ))}
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
                    className="w-full rounded-xl border p-2.5"
                  >
                    <option value="">All Categories</option>
                    <option value="vegetable">🥬 Vegetables</option>
                    <option value="herb">🌿 Herbs</option>
                    <option value="fruit">🍎 Fruits</option>
                    <option value="flower">🌸 Flowers</option>
                    <option value="tree">🌳 Trees</option>
                  </select>
                </div>

                {/* Plant Type Filter - NEW */}
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                    <Thermometer className="w-4 h-4" style={{ color: tropical.plum.primary }} />
                    Plant Type
                  </label>
                  <select
                    value={filters.plant_type}
                    onChange={(e) => setFilters(f => ({ ...f, plant_type: e.target.value }))}
                    className="w-full rounded-xl border p-2.5"
                  >
                    <option value="">All Types</option>
                    <option value="annual">🌱 Annual</option>
                    <option value="perennial">🌳 Perennial</option>
                  </select>
                </div>

                {/* Sun Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Sun Requirement</label>
                  <select
                    value={filters.sun}
                    onChange={(e) => setFilters(f => ({ ...f, sun: e.target.value }))}
                    className="w-full rounded-xl border p-2.5"
                  >
                    <option value="">Any</option>
                    <option value="full_sun">☀️ Full Sun</option>
                    <option value="partial_shade">⛅ Partial Shade</option>
                    <option value="full_shade">🌥️ Full Shade</option>
                  </select>
                </div>

                {/* Water Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Water Needs</label>
                  <select
                    value={filters.water}
                    onChange={(e) => setFilters(f => ({ ...f, water: e.target.value }))}
                    className="w-full rounded-xl border p-2.5"
                  >
                    <option value="">Any</option>
                    <option value="low">💧 Low</option>
                    <option value="moderate">💧💧 Moderate</option>
                    <option value="high">💧💧💧 High</option>
                  </select>
                </div>

                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={() => {
                    setFilters({ category: "", sun: "", water: "", plant_type: "", zone: "" });
                    setSearchQuery("");
                  }}
                >
                  Clear Filters
                </Button>
              </CardContent>
            </Card>

            {/* Spacing Calculator */}
            {viewMode === "grid" && (
              <Card className="border-0 shadow-lg" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <CardHeader>
                  <CardTitle className="text-lg" style={{ color: tropical.mango.primary }}>
                    Spacing Calculator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Area (sq ft)</label>
                    <Input
                      type="number"
                      value={spacingCalc.area}
                      onChange={(e) => setSpacingCalc(prev => ({ ...prev, area: parseFloat(e.target.value) || 0 }))}
                      className="rounded-xl"
                    />
                  </div>
                  {selectedPlant && (
                    <>
                      <Button
                        className="w-full rounded-xl text-white"
                        onClick={() => calculateSpacing(selectedPlant.name)}
                        style={{ background: tropical.green.primary }}
                      >
                        Calculate for {selectedPlant.name}
                      </Button>
                      {spacingCalc.result && (
                        <div 
                          className="p-3 rounded-xl text-sm"
                          style={{ background: tropical.green.light }}
                        >
                          <p className="font-medium" style={{ color: tropical.green.dark }}>
                            {spacingCalc.result.message}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {viewMode === "grid" ? (
              /* Plant Grid View */
              loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: tropical.green.primary }} />
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-4">
                    Showing {filteredPlants.length} plants
                    {filters.zone && ` in Zone ${filters.zone}`}
                  </p>

                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredPlants.map((plant) => (
                      <motion.div
                        key={plant.id || plant.name}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        className="cursor-pointer"
                      >
                        <Card
                          className={`transition-all border-0 shadow-lg overflow-hidden ${selectedPlant?.id === plant.id ? 'ring-2' : ''}`}
                          style={{ 
                            background: 'rgba(255,255,255,0.95)',
                            ringColor: tropical.green.primary
                          }}
                          onClick={() => openPlantDetails(plant)}
                        >
                          {/* Colored top bar based on category */}
                          <div 
                            className="h-1.5"
                            style={{
                              background: plant.category === 'fruit' 
                                ? `linear-gradient(90deg, ${tropical.guava.primary}, ${tropical.mango.primary})`
                                : plant.category === 'tree'
                                  ? `linear-gradient(90deg, ${tropical.green.dark}, ${tropical.green.primary})`
                                  : plant.category === 'herb'
                                    ? `linear-gradient(90deg, ${tropical.green.vibrant}, ${tropical.green.primary})`
                                    : plant.category === 'flower'
                                      ? `linear-gradient(90deg, ${tropical.plum.primary}, ${tropical.plum.light})`
                                      : `linear-gradient(90deg, ${tropical.green.primary}, ${tropical.green.vibrant})`
                            }}
                          />
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {getCategoryIcon(plant.category)}
                                <div>
                                  <h3 className="font-semibold" style={{ color: tropical.green.dark }}>
                                    {plant.name}
                                  </h3>
                                  <p className="text-xs text-gray-400 italic">{plant.scientific_name}</p>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <span 
                                  className="text-xs capitalize px-2 py-0.5 rounded-full"
                                  style={{ 
                                    background: tropical.green.light,
                                    color: tropical.green.dark
                                  }}
                                >
                                  {plant.category}
                                </span>
                                {plant.plant_type && (
                                  <span 
                                    className="text-xs px-2 py-0.5 rounded-full"
                                    style={{ 
                                      background: plant.plant_type === 'perennial' ? tropical.plum.light : tropical.mango.light,
                                      color: plant.plant_type === 'perennial' ? tropical.plum.primary : tropical.mango.primary
                                    }}
                                  >
                                    {plant.plant_type}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 mt-3 text-sm">
                              <span title="Sun requirement">{getSunIcon(plant.sun_requirement)}</span>
                              <span title="Water needs">{getWaterIcon(plant.water_needs)}</span>
                              <span className="flex items-center gap-1 text-gray-500">
                                <Clock className="w-3 h-3" />
                                {plant.days_to_harvest || '?'} days
                              </span>
                            </div>

                            {/* Planting & Harvest Preview */}
                            {(plant.planting_months?.length > 0 || plant.harvest_months?.length > 0) && (
                              <div className="mt-3 pt-3 border-t border-gray-100 text-xs">
                                {plant.planting_months?.length > 0 && (
                                  <div className="flex items-center gap-1 mb-1">
                                    <span>🌱</span>
                                    <span className="text-gray-500">Plant:</span>
                                    <span className="font-medium" style={{ color: tropical.green.dark }}>
                                      {plant.planting_months.map(m => MONTHS[m-1]).join(', ')}
                                    </span>
                                  </div>
                                )}
                                {plant.harvest_months?.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <span>🥬</span>
                                    <span className="text-gray-500">Harvest:</span>
                                    <span className="font-medium" style={{ color: tropical.mango.primary }}>
                                      {plant.harvest_months.map(m => MONTHS[m-1]).join(', ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* USDA Zones */}
                            {plant.usda_zones?.length > 0 && (
                              <div className="flex items-center gap-1 mt-2">
                                <MapPin className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-400">
                                  Zones: {plant.usda_zones.join(', ')}
                                </span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </>
              )
            ) : (
              /* Harvest Calendar View */
              <div ref={printRef}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 
                      className="text-2xl font-bold"
                      style={{ color: tropical.green.dark }}
                    >
                      🗓️ Harvest Calendar
                    </h2>
                    <p className="text-sm text-gray-500">
                      {filters.zone ? `USDA Zone ${filters.zone}` : 'All Zones'}
                      {filters.category && ` • ${filters.category.charAt(0).toUpperCase() + filters.category.slice(1)}s`}
                    </p>
                  </div>
                  <Button
                    onClick={handlePrintCalendar}
                    className="rounded-xl text-white"
                    style={{ background: tropical.green.primary }}
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print Calendar
                  </Button>
                </div>

                {loadingCalendar ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: tropical.mango.primary }} />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                      <thead>
                        <tr>
                          <th 
                            className="p-3 text-left text-white font-semibold"
                            style={{ background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})` }}
                          >
                            Plant
                          </th>
                          <th 
                            className="p-3 text-left text-white font-semibold"
                            style={{ background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})` }}
                          >
                            Type
                          </th>
                          {MONTHS.map(m => (
                            <th 
                              key={m}
                              className="p-2 text-center text-white font-semibold text-xs"
                              style={{ background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})` }}
                            >
                              {m}
                            </th>
                          ))}
                          <th 
                            className="p-3 text-center text-white font-semibold"
                            style={{ background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})` }}
                          >
                            Days
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {calendarData.map((plant, idx) => (
                          <tr 
                            key={plant.name}
                            className={`border-b hover:bg-gray-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                            onClick={() => openPlantDetails(plant)}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {getCategoryIcon(plant.category)}
                                <span className="font-medium" style={{ color: tropical.green.dark }}>
                                  {plant.name}
                                </span>
                              </div>
                            </td>
                            <td className="p-3">
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ 
                                  background: plant.plant_type === 'perennial' ? tropical.plum.light : tropical.mango.light,
                                  color: plant.plant_type === 'perennial' ? tropical.plum.primary : tropical.mango.primary
                                }}
                              >
                                {plant.plant_type || 'annual'}
                              </span>
                            </td>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(monthNum => {
                              const isPlanting = plant.planting_months?.includes(monthNum);
                              const isHarvest = plant.harvest_months?.includes(monthNum);
                              return (
                                <td 
                                  key={monthNum}
                                  className="p-2 text-center"
                                  style={{
                                    background: isPlanting 
                                      ? tropical.green.light 
                                      : isHarvest 
                                        ? tropical.mango.light 
                                        : 'transparent'
                                  }}
                                >
                                  {isPlanting && <span title="Plant">🌱</span>}
                                  {isHarvest && <span title="Harvest">🥬</span>}
                                </td>
                              );
                            })}
                            <td className="p-3 text-center font-medium" style={{ color: tropical.green.dark }}>
                              {plant.days_to_harvest || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Legend */}
                    <div className="flex items-center gap-6 mt-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: tropical.green.light }}>
                          🌱
                        </div>
                        <span className="text-gray-600">Planting Time</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: tropical.mango.light }}>
                          🥬
                        </div>
                        <span className="text-gray-600">Harvest Time</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Plant Detail Modal */}
      <AnimatePresence>
        {showPlantModal && selectedPlant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowPlantModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
              style={{ boxShadow: `0 25px 50px ${tropical.green.primary}30` }}
            >
              {/* Header */}
              <div 
                className="p-6 text-white rounded-t-3xl"
                style={{
                  background: `linear-gradient(135deg, ${tropical.green.dark}, ${tropical.green.primary})`
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getCategoryIcon(selectedPlant.category)}
                      <Badge className="bg-white/20 text-white">
                        {selectedPlant.category}
                      </Badge>
                      {selectedPlant.plant_type && (
                        <Badge className="bg-white/20 text-white">
                          {selectedPlant.plant_type}
                        </Badge>
                      )}
                    </div>
                    <h2 className="text-3xl font-bold mb-1">{selectedPlant.name}</h2>
                    <p className="text-white/70 italic">{selectedPlant.scientific_name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowPlantModal(false)}
                    className="text-white hover:bg-white/20 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-xl" style={{ background: tropical.green.light }}>
                    <span className="text-2xl">{getSunIcon(selectedPlant.sun_requirement)}</span>
                    <p className="text-xs mt-1 capitalize text-gray-600">
                      {selectedPlant.sun_requirement?.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-xl" style={{ background: tropical.mango.light }}>
                    <span className="text-xl">{getWaterIcon(selectedPlant.water_needs)}</span>
                    <p className="text-xs mt-1 capitalize text-gray-600">
                      {selectedPlant.water_needs} water
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-xl" style={{ background: tropical.plum.light }}>
                    <span className="text-xl font-bold" style={{ color: tropical.plum.primary }}>
                      {selectedPlant.days_to_harvest}
                    </span>
                    <p className="text-xs mt-1 text-gray-600">days to harvest</p>
                  </div>
                </div>

                {/* Planting & Harvest Times */}
                {(selectedPlant.planting_months?.length > 0 || selectedPlant.harvest_months?.length > 0) && (
                  <div 
                    className="rounded-2xl p-5"
                    style={{ background: `linear-gradient(135deg, ${tropical.green.light}50, ${tropical.mango.light}50)` }}
                  >
                    <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: tropical.green.dark }}>
                      <Calendar className="w-5 h-5" />
                      Planting & Harvest Schedule
                    </h3>
                    <div className="grid grid-cols-12 gap-1 mb-2">
                      {MONTHS.map((m, i) => (
                        <div key={m} className="text-center text-xs text-gray-500">{m}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-12 gap-1 mb-2">
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <div 
                          key={`plant-${m}`}
                          className="h-6 rounded flex items-center justify-center text-xs"
                          style={{
                            background: selectedPlant.planting_months?.includes(m) ? tropical.green.primary : 'transparent'
                          }}
                        >
                          {selectedPlant.planting_months?.includes(m) && <span>🌱</span>}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-12 gap-1">
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <div 
                          key={`harvest-${m}`}
                          className="h-6 rounded flex items-center justify-center text-xs"
                          style={{
                            background: selectedPlant.harvest_months?.includes(m) ? tropical.mango.primary : 'transparent'
                          }}
                        >
                          {selectedPlant.harvest_months?.includes(m) && <span>🥬</span>}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 mt-3 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded" style={{ background: tropical.green.primary }}></span>
                        Plant
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-4 h-4 rounded" style={{ background: tropical.mango.primary }}></span>
                        Harvest
                      </span>
                    </div>
                  </div>
                )}

                {/* Growing Guide */}
                {selectedPlant.growing_guide && (
                  <div className="rounded-2xl p-5 bg-white border">
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: tropical.green.dark }}>
                      <BookOpen className="w-5 h-5" style={{ color: tropical.green.primary }} />
                      Growing Guide
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{selectedPlant.growing_guide}</p>
                  </div>
                )}

                {/* Care Tips */}
                {selectedPlant.care_tips && (
                  <div 
                    className="rounded-2xl p-5"
                    style={{ background: `${tropical.mango.light}50` }}
                  >
                    <h3 className="font-bold mb-3 flex items-center gap-2" style={{ color: tropical.mango.primary }}>
                      <Info className="w-5 h-5" />
                      Care Tips
                    </h3>
                    <p className="text-gray-700">{selectedPlant.care_tips}</p>
                  </div>
                )}

                {/* Planting Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-4 rounded-xl bg-gray-50">
                    <span className="text-gray-500">Spacing:</span>
                    <span className="ml-2 font-semibold">{selectedPlant.spacing_inches}&quot; apart</span>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50">
                    <span className="text-gray-500">Depth:</span>
                    <span className="ml-2 font-semibold">{selectedPlant.planting_depth_inches}&quot;</span>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50">
                    <span className="text-gray-500">Germination:</span>
                    <span className="ml-2 font-semibold">{selectedPlant.germination_days} days</span>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50">
                    <span className="text-gray-500">Season:</span>
                    <span className="ml-2 font-semibold capitalize">{selectedPlant.growing_season}</span>
                  </div>
                </div>

                {/* USDA Zones */}
                {selectedPlant.usda_zones?.length > 0 && (
                  <div className="p-4 rounded-xl bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4" style={{ color: tropical.guava.primary }} />
                      <span className="text-gray-500">USDA Hardiness Zones:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlant.usda_zones.map(z => (
                        <Badge 
                          key={z} 
                          className="rounded-full"
                          style={{ background: tropical.green.light, color: tropical.green.dark }}
                        >
                          Zone {z}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedPlant.frost_tolerant && (
                  <p className="text-sm" style={{ color: '#3B82F6' }}>
                    ❄️ This plant is frost tolerant
                  </p>
                )}

                {/* Companion Plants */}
                {selectedPlant.companion_plants?.length > 0 && (
                  <div className="p-4 rounded-xl" style={{ background: `${tropical.green.light}50` }}>
                    <p className="text-sm font-semibold flex items-center gap-1 mb-2" style={{ color: tropical.green.dark }}>
                      <Users className="w-4 h-4" /> Good companions:
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedPlant.companion_plants.join(", ")}
                    </p>
                  </div>
                )}

                {selectedPlant.incompatible_plants?.length > 0 && (
                  <div className="p-4 rounded-xl bg-red-50">
                    <p className="text-sm font-semibold flex items-center gap-1 mb-2 text-red-700">
                      <XCircle className="w-4 h-4" /> Avoid planting with:
                    </p>
                    <p className="text-sm text-red-600">
                      {selectedPlant.incompatible_plants.join(", ")}
                    </p>
                  </div>
                )}

                {/* Add to Design Button */}
                <Button
                  onClick={() => {
                    setShowPlantModal(false);
                    navigate('/design');
                  }}
                  className="w-full rounded-xl h-12 text-white font-semibold"
                  style={{ background: `linear-gradient(135deg, ${tropical.green.primary}, ${tropical.green.dark})` }}
                >
                  <Sprout className="w-5 h-5 mr-2" />
                  Add to Garden Design
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlantDatabasePage;
